import { setSnackbarForEndpoint } from '../../utils/snackbar';
import { sepAPI } from '../API/sepAPI';
import {
  Department,
  DepartmentExtended,
  CreateDepartmentBody,
  UpdateDepartmentBody,
  reOrderDepartmentBody
} from '../../../shared/types/Department';
import { DefaultStakeholderExtended } from '../../../shared/types/DefaultStakeholder';

export const departmentSlice = sepAPI.injectEndpoints({
  endpoints: (builder) => ({
    getDepartments: builder.query<Department[], void>({
      query: () => `departments`,
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the list of departments',
        });
      },
    }),

    createDepartment: builder.mutation<Department, {data: CreateDepartmentBody, weightageList: any}>({
      query: ({data, weightageList}) => {
        return {
          url: `departments`,
          method: `POST`,
          body: { ...data, weightageList },
        };
      },
      invalidatesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        const deptName = (arg as any).data?.name || 'Department';
        setSnackbarForEndpoint(api, {
          successMessage: `Department '${deptName}' created successfully!`,
          errorMessage: 'There was a problem creating your Department.',
        });
      },
    }),

    editDepartment: builder.mutation<
      Department,
      { data: UpdateDepartmentBody; id: number; weightageList: any }
    >({
      query: ({ data, id, weightageList }) => {
        return {
          url: `department/${id}`,
          method: 'PATCH',
          body: {
            ...data,
            weightageList
          },
        };
      },
      invalidatesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        const deptName = (arg as any).data?.name || 'Department';
        setSnackbarForEndpoint(api, {
          successMessage: `Department '${deptName}' updated successfully!`,
          errorMessage: 'There was a problem updating your Department.',
        });
      },
    }),

    editDepartmentDisplayOrderId: builder.mutation<
      Department,
      { data: { departmentDisplayOrderId: number | null }; id: number }
    >({
      query: ({ data, id }) => {
        return {
          url: `department-display-order/${id}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        const deptName = (arg as any).data?.name || 'Department';
        setSnackbarForEndpoint(api, {
          successMessage: `Department '${deptName}' updated successfully!`,
          errorMessage: 'There was a problem updating your Department.',
        });
      },
    }),

    reOrderDepartment: builder.mutation<
      void,
      reOrderDepartmentBody
    >({
      query: ({ payload }) => ({
          url: 'department-order',
          method: 'PATCH',
          body: payload
        }),
      invalidatesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department order updated successfully!',
          errorMessage: 'There was a problem in updating your Department order.',
        });
      },
    }),

    deleteDepartment: builder.mutation<void, number>({
      query: (id) => {
        return {
          url: `department/${id}`,
          method: `DELETE`,
        };
      },
      invalidatesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          successMessage: 'Department deleted successfully!',
          errorMessage: 'There was a problem deleting your Department.',
        }, 5000);
      },
    }),

    getDepartmentsExtended: builder.query<DepartmentExtended[], void>({
      query: () => `departments-extended`,
      providesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the list of departments',
        });
      },
    }),
    getUserDisplayNames: builder.query<DepartmentExtended[], void>({
      query: () => `user-names`,
      providesTags: ['Departments'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the list of departments',
        });
      },
    }),
    getDefaultStakeholdersForSEP: builder.query<
      DefaultStakeholderExtended[],
      number
    >({
      query: (sepID: number) => `default-stakeholders/${sepID}`,
      providesTags: ['DefaultStakeholdersForSEP'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage:
            'There was a problem getting the list of default stakeholders for this SEP',
        });
      },
    }),
    setDefaultStakeholderForSEPDepartment: builder.mutation<
      void,
      { sepID: number; departmentID: number; userID: string; stakeholderName?: string }
    >({
      query: ({ sepID, departmentID, userID }) => ({
          url: `default-stakeholder/${sepID}/dept/${departmentID}`,
          method: 'PATCH',
          body: { userID },
        }), 
      invalidatesTags: ['DefaultStakeholdersForSEP', 'TasksForSEP'],
      onQueryStarted: (arg, api) => {
        const stakeholderName = (arg as any).stakeholderName;
        setSnackbarForEndpoint(api, {
          successMessage: stakeholderName ? `Default stakeholder updated to ${stakeholderName} successfully!` : 'Default stakeholder updated successfully!',
          errorMessage: 'There was a problem setting the default stakeholder for this department',
        }, 12000);
      },
    }),
  }),
});

export const {
  useGetDepartmentsQuery,
  useGetDefaultStakeholdersForSEPQuery,
  useSetDefaultStakeholderForSEPDepartmentMutation,
  useGetDepartmentsExtendedQuery,
  useGetUserDisplayNamesQuery,
  useCreateDepartmentMutation,
  useDeleteDepartmentMutation,
  useEditDepartmentMutation,
  useEditDepartmentDisplayOrderIdMutation,
  useReOrderDepartmentMutation
} = departmentSlice;
